// Tiny in-tab notification primitives for the admin dashboard.
// - playChime(): a short pleasant 2-note ping using WebAudio (no asset to ship)
// - flashFavicon(): toggles the page favicon between normal and red-dot for
//   ~10 seconds so an admin with a different tab focused sees the alert.
// - notify(): browser-level Notification popup when the tab is in background.

let audioCtx = null;

export function playChime() {
  try {
    if (!audioCtx) {
      const Ctor = window.AudioContext || window.webkitAudioContext;
      if (!Ctor) return;
      audioCtx = new Ctor();
    }
    if (audioCtx.state === "suspended") audioCtx.resume();
    const now = audioCtx.currentTime;
    const notes = [
      { freq: 880, time: 0.0, dur: 0.18 },   // A5
      { freq: 1318.5, time: 0.18, dur: 0.22 }, // E6
    ];
    notes.forEach(({ freq, time, dur }) => {
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + time);
      gain.gain.exponentialRampToValueAtTime(0.18, now + time + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + time + dur);
      osc.connect(gain).connect(audioCtx.destination);
      osc.start(now + time);
      osc.stop(now + time + dur);
    });
  } catch {
    /* ignore */
  }
}

let faviconTimer = null;
let originalFavicon = null;

export function flashFavicon() {
  try {
    const link = document.querySelector("link[rel='icon']");
    if (!link) return;
    if (!originalFavicon) originalFavicon = link.href;
    const canvas = document.createElement("canvas");
    canvas.width = 32; canvas.height = 32;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#E11D48";
    ctx.beginPath();
    ctx.arc(16, 16, 13, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "bold 18px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("!", 16, 17);
    link.href = canvas.toDataURL("image/png");
    if (faviconTimer) clearTimeout(faviconTimer);
    faviconTimer = setTimeout(() => {
      if (originalFavicon) link.href = originalFavicon;
      faviconTimer = null;
    }, 10000);
  } catch {
    /* ignore */
  }
}

export function browserNotify(title, body, onClick) {
  try {
    if (!("Notification" in window)) return;
    if (Notification.permission !== "granted") return;
    if (document.visibilityState === "visible") return; // skip if user is looking
    const n = new Notification(title, { body, icon: "/logo192.png", tag: "live-chat" });
    if (onClick) n.onclick = onClick;
  } catch {
    /* ignore */
  }
}

const PREFS_KEY = "gc_admin_notif_prefs";
const defaults = {
  enabled: true,
  sound: true,
  desktop: true,
  flash_favicon: true,
  quiet_start: "",
  quiet_end: "",
  events: { new_chat: true, new_submission: false },
};

export function getNotifPrefs() {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return { ...defaults };
    return { ...defaults, ...JSON.parse(raw), events: { ...defaults.events, ...(JSON.parse(raw).events || {}) } };
  } catch {
    return { ...defaults };
  }
}

export function setNotifPrefs(prefs) {
  localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
}

function _isQuietHour(prefs) {
  const { quiet_start, quiet_end } = prefs;
  if (!quiet_start || !quiet_end) return false;
  const now = new Date();
  const minutes = now.getHours() * 60 + now.getMinutes();
  const [sh, sm] = quiet_start.split(":").map(Number);
  const [eh, em] = quiet_end.split(":").map(Number);
  const start = sh * 60 + sm;
  const end = eh * 60 + em;
  // Wraps midnight, e.g. 22:00 → 07:00
  return start <= end ? (minutes >= start && minutes < end) : (minutes >= start || minutes < end);
}

export function alertForEvent(event, title, body, onClick) {
  const prefs = getNotifPrefs();
  if (!prefs.enabled) return;
  if (prefs.events && prefs.events[event] === false) return;
  if (_isQuietHour(prefs)) return;
  if (prefs.sound) playChime();
  if (prefs.flash_favicon) flashFavicon();
  if (prefs.desktop) browserNotify(title, body, onClick);
}
